import { Row, Button, Col, AutoComplete, Input, Form, Alert } from 'antd';
import deepEqual from 'components/utils/deepEqual';
import React, { useEffect, useState } from 'react';
import { CREATE_NEW, ListChoice, ListChoiceItem } from 'services/hitchit/types/Choices';

const CREATE_NEW_LABEL = '<start typing>';

type Option = {
  label: string;
  value: string;
};

function createNewOption(label = CREATE_NEW_LABEL): Option {
  return {
    label: `Create new one: ${label === CREATE_NEW ? CREATE_NEW_LABEL : label}`,
    value: CREATE_NEW,
  };
}

const ConfigureListField = ({
  choice,
  preference,
  savedPreference,
  onChange,
  onReset,
  onClear,
  onCancel,
  onSave,
}: {
  choice: ListChoice;
  preference?: ListChoiceItem;
  savedPreference?: ListChoiceItem;
  onChange: (choice: ListChoiceItem | undefined) => void;
  onReset: () => void;
  onClear: () => void;
  onCancel: () => void;
  onSave: () => void;
}): JSX.Element => {
  const isRequired = choice?.required && !preference;

  // const [options, setOptions] = useState<{ label: string; value: string }[]>(
  //   createOptions(manualChoice.jsonObject)
  // );
  const [value, setValue] = useState<string>();
  const [options, setOptions] = useState<Option[]>();

  useEffect(() => {
    if (choice) {
      const createOption = choice.create ? createNewOption() : undefined;
      setOptions([
        ...choice.choices.map(({ label, id }) => ({ label, value: id })),
        ...(createOption ? [createOption] : ([] as Option[])),
      ]);
    }
  }, [choice]);

  useEffect(() => {
    setValue(preference ? preference.label : '');
  }, [preference]);

  const onChangeLocal = (query: string) => {
    if (choice.create) {
      setOptions([...(options ? options.slice(0, -1) : []), createNewOption(query)]);
    }
    setValue(query);

    if (!query) {
      onChange(undefined);
    }
  };

  const onSelect = (selectedValue?: string) => {
    const existingChoice = choice?.choices.find(({ id }) => id === selectedValue);

    if (!existingChoice && !value) {
      // user should first type in something
      setValue('');

      return;
    }

    onChange(
      existingChoice ||
        ({
          id: CREATE_NEW,
          label: value,
        } as ListChoiceItem)
    );

    setValue('');
  };

  // const { tags, inputVisible, inputValue, editInputIndex, editInputValue } = this.state;
  return (
    <Row gutter={[8, 8]} style={{ textAlign: 'left' }}>
      <Col span={24}>
        {choice ? (
          <Form.Item validateStatus={isRequired ? 'error' : ''} help={isRequired ? 'Required' : ''}>
            <AutoComplete
              allowClear
              style={{ width: 200 }}
              dropdownMatchSelectWidth={500}
              placeholder={choice?.label}
              options={options}
              value={value}
              onChange={onChangeLocal}
              onSelect={onSelect}
              filterOption={(inputValue, option) => {
                return (
                  !!option?.label &&
                  (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                );
              }}
            >
              <Input
                onPressEnter={() => {
                  onSelect(value);
                }}
              />
            </AutoComplete>
          </Form.Item>
        ) : undefined}
      </Col>
      {choice.infoText && (
        <Col>
          <Alert
            style={{ padding: 16, margin: 16 }}
            message={choice.infoText}
            type="info"
            showIcon
          />
        </Col>
      )}
      <Col>
        <Button type="primary" disabled={deepEqual(savedPreference, preference)} onClick={onSave}>
          Save changes
        </Button>
      </Col>
      <Col>
        <Button disabled={deepEqual(savedPreference, preference)} type="ghost" onClick={onCancel}>
          Cancel change
        </Button>
      </Col>
      {choice.default && (
        <Col>
          <Button type="ghost" onClick={onReset}>
            Pick default
          </Button>
        </Col>
      )}
      <Col>
        <Button type="ghost" onClick={onClear}>
          Clear
        </Button>
      </Col>
    </Row>
  );
};

export default ConfigureListField;
